import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Divider, useMediaQuery, useTheme } from '@mui/material';
import { WarningCircle } from '@phosphor-icons/react';
import './index.css';

import { BeneficiaryDataCardAllocationErrorStyled } from './BeneficiariesDataCardStyled';
import {
  updateBeneficiaryAllocation,
  deleteBeneficiary,
} from 'enrollment/features/AddYourBeneficiaries/services';
import { PSModalWrapper } from 'enrollment/components/Shared';
import { BeneficiariesDto } from 'enrollment/queries/useEnrollmentAddBeneficiaryQuery/types';
import { PsButtonTuner, PsNumericTuner } from 'stencilSetup';

export interface BeneficiaryDataCardProps {
  orgBenefitId: number;
  beneficiaryDetail: BeneficiariesDto;
}

export const BeneficiaryDataCard = ({
  orgBenefitId,
  beneficiaryDetail,
}: BeneficiaryDataCardProps) => {
  const dispatch = useDispatch();

  const theme = useTheme();
  const desktopViewConditions = useMediaQuery(theme.breakpoints.up('sm'));

  const { t } = useTranslation('pages.enrollmentAddBeneficiaries');

  const [showError, setShowError] = useState({
    status: false,
    message: '',
  });

  const handleConfirmDeleteModal = () => {
    dispatch(deleteBeneficiary({ orgBenefitId, beneficiaryId }));
  };

  const handleAllocationChange = (e: any) => {
    const value = e.target.value.toString().split('.')[0].replace('%', '');
    const fieldName = e.target.name;
    if (!isNaN(value)) {
      const parsedValue = parseInt(value);
      if (parsedValue > 100) {
        setShowError({
          status: true,
          message: t('allocationGreaterThanHundred'),
        });
      } else if (parsedValue <= 0) {
        setShowError({
          status: true,
          message: t('allocationLessThanZero'),
        });
      } else {
        setShowError({ status: false, message: '' });
      }
      dispatch(updateBeneficiaryAllocation({ orgBenefitId, value, fieldName }));
    }
  };

  const handleKeyPressAllocationChange = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    const charCode = e.charCode;
    const char = String.fromCharCode(charCode);

    // Check if the character is a decimal point
    if (char === '.') {
      setShowError({
        status: true,
        message: t('allocationWholeNumber'),
      });
      e.preventDefault();
      return;
    }

    // Allow only digits (0-9)
    if (charCode < 48 || charCode > 57) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    parseInt(beneficiaryDetail?.allocation) === 0 &&
      setShowError({
        status: true,
        message: t('allocationLessThanZero'),
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const beneficiaryId = `${beneficiaryDetail.id ? beneficiaryDetail.id : beneficiaryDetail.uniq_id}`;

  const allocationPercent = Number(
    beneficiaryDetail?.allocation?.split('.')[0]
  );

  return (
    <>
      <div
        className="card border shadow-none px-0 py-md-2_5 d-md-flex align-items-md-center my-md-2"
        id={`beneficiaryDetail_${beneficiaryId}`}
      >
        <div className="d-flex flex-column flex-md-row col-auto col-md-3 col-lg px-5 pl-md-4 pr-md-0 position-relative">
          {!desktopViewConditions && (
            // Button for mobile view
            <PsButtonTuner
              buttonId={`delete-dependent-${orgBenefitId}-${beneficiaryId}`}
              type="button"
              label={t('common:delete')}
              isIcon={true}
              icon="trash"
              level="tertiary"
              data-testid={`delete-dependent-${orgBenefitId}-${beneficiaryId}`}
              rootClass="position-absolute top-0 end-0 mr-3"
              otherData={[
                { attribute: 'data-bs-toggle', value: 'modal' },
                {
                  attribute: 'data-bs-target',
                  value: `#primary-delete-modal-${orgBenefitId}-${beneficiaryId}`,
                },
                { attribute: 'data-bs-dismiss', value: 'modal' },
              ]}
            />
          )}

          <div className={desktopViewConditions ? 'body1' : 'headline-3'}>
            {beneficiaryDetail?.name}
          </div>
          {!desktopViewConditions && (
            <div className="body2">{beneficiaryDetail?.relationship}</div>
          )}
        </div>
        {!desktopViewConditions && <hr />}
        <div className="col-auto col-md col-lg-auto d-md-flex align-items-md-center pr-lg-4">
          <div className="d-flex align-items-center mx-3 gap-3">
            {!desktopViewConditions && (
              <div className="col body2 text-left px-0">
                {t('definedAllocationMobileViewText')}
              </div>
            )}
            <div className="col-auto d-flex flex-column px-0">
              <div
                className="d-flex flex-row-reverse percentage-input-field-container"
                data-testid={
                  beneficiaryDetail?.primary_beneficiary
                    ? `primary-percentage-field`
                    : `secondary-percentage-field`
                }
              >
                <PsNumericTuner
                  data-testid={`beneficiary-allocation-input-${beneficiaryId}`}
                  inputId={`input-${beneficiaryId}`}
                  label="beneficiary allocation"
                  name={`input-${beneficiaryId}`}
                  hide-label
                  min={0}
                  max={100}
                  value={allocationPercent}
                  onKeyUp={(e) => handleKeyPressAllocationChange(e as any)}
                  onBlur={handleAllocationChange}
                  validation-text="must be a number must be between 0 and 100 and can have up to 2 decimal places."
                  extraClass="total-allocation-text-field disable-increment text-center"
                  icon="percent"
                />
              </div>
              {showError.status && (
                <BeneficiaryDataCardAllocationErrorStyled
                  variant="caption"
                  display="block"
                >
                  <WarningCircle size={24} />
                  {'  ' + showError.message}
                </BeneficiaryDataCardAllocationErrorStyled>
              )}
            </div>
          </div>

          {desktopViewConditions && (
            <>
              <Divider
                orientation="vertical"
                variant="middle"
                flexItem
                className="m-0"
                aria-hidden="true"
                role="presentation"
              />
              <PsButtonTuner
                buttonId={`delete-dependent-${orgBenefitId}-${beneficiaryId}`}
                type="button"
                label={t('common:delete')}
                icon="trash"
                level="tertiary"
                data-testid={`delete-dependent-${orgBenefitId}-${beneficiaryId}`}
                otherData={[
                  { attribute: 'data-bs-toggle', value: 'modal' },
                  {
                    attribute: 'data-bs-target',
                    value: `#primary-delete-modal-${orgBenefitId}-${beneficiaryId}`,
                  },
                  { attribute: 'data-bs-dismiss', value: 'modal' },
                ]}
              />
            </>
          )}
        </div>
      </div>

      <PSModalWrapper
        data-testid={`primary-delete-modal-${orgBenefitId}-${beneficiaryId}`}
        id={`primary-delete-modal-${orgBenefitId}-${beneficiaryId}`}
        title={`${t('common:remove')} ${beneficiaryDetail?.name}?`}
        message={`${t('deleteModalConfirmationDescription1')} ${
          beneficiaryDetail?.name
        }. <br><br> ${t('deleteModalConfirmationDescription2')}`}
        primaryButton={t('common:confirm')}
        primaryActive={true}
        secondaryButton={t('common:cancel')}
        onModalDismiss={handleConfirmDeleteModal}
      ></PSModalWrapper>
    </>
  );
};
